import React, { useState, useEffect } from "react";
import { apiFetchJson } from "../api";
import {
  RefreshIcon,
  TrashIcon,
  GlobeIcon,
  MapPinIcon,
  BuildingIcon,
  XIcon,
  ArmorLogoIcon,
  Spinner
} from "./Icons";
import { t, tf, locale } from "../i18n";
import { A, aa, S, sa } from "../theme/palette";

// Panel-wide sans stack — matches the rest of the kabinett, replaces the
// generic ui-monospace previously used for technical strings.
const PANEL_FONT = "'Manrope', 'Sora', 'Inter', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif";
// CSS feature settings that keep digits the same width so IPs/ports/coords stay
// crisply aligned without dropping into a true monospace face.
const TABULAR = {
  fontFeatureSettings: '"tnum" 1, "ss01" 1, "cv01" 1',
  fontVariantNumeric: "tabular-nums"
};
const PANEL_TABULAR_TEXT = {
  fontFamily: PANEL_FONT,
  ...TABULAR
};

// Map a full country name (as some geo APIs report it — e.g. "Germany"
// from ip-api.com) to its ISO 3166-1 alpha-2 code. Without this mapping a
// naive .slice(0, 2) on "Germany" yields "GE" — which produces the
// GEORGIA flag (🇬🇪) instead of the German one (🇩🇪). The list covers the
// countries the panel realistically sees on residential-proxy egress; any
// missing entry falls back to the globe glyph rather than a wrong flag.
const COUNTRY_NAME_TO_CODE = {
  GERMANY: "DE",
  DEUTSCHLAND: "DE",
  AUSTRIA: "AT",
  ÖSTERREICH: "AT",
  OESTERREICH: "AT",
  SWITZERLAND: "CH",
  SCHWEIZ: "CH",
  FRANCE: "FR",
  FRANKREICH: "FR",
  NETHERLANDS: "NL",
  NEDERLAND: "NL",
  NIEDERLANDE: "NL",
  POLAND: "PL",
  POLEN: "PL",
  POLSKA: "PL",
  DENMARK: "DK",
  DÄNEMARK: "DK",
  DANEMARK: "DK",
  DANMARK: "DK",
  ITALY: "IT",
  ITALIEN: "IT",
  ITALIA: "IT",
  SPAIN: "ES",
  SPANIEN: "ES",
  ESPAÑA: "ES",
  ESPANA: "ES",
  PORTUGAL: "PT",
  BELGIUM: "BE",
  BELGIEN: "BE",
  CZECHIA: "CZ",
  "CZECH REPUBLIC": "CZ",
  TSCHECHIEN: "CZ",
  SWEDEN: "SE",
  SCHWEDEN: "SE",
  NORWAY: "NO",
  NORWEGEN: "NO",
  FINLAND: "FI",
  FINNLAND: "FI",
  IRELAND: "IE",
  IRLAND: "IE",
  HUNGARY: "HU",
  UNGARN: "HU",
  ROMANIA: "RO",
  RUMÄNIEN: "RO",
  GREECE: "GR",
  GRIECHENLAND: "GR",
  TURKEY: "TR",
  TÜRKEI: "TR",
  "UNITED KINGDOM": "GB",
  "GREAT BRITAIN": "GB",
  ENGLAND: "GB",
  "UNITED STATES": "US",
  USA: "US",
  RUSSIA: "RU",
  RUSSLAND: "RU",
  UKRAINE: "UA",
  BULGARIA: "BG",
  BULGARIEN: "BG",
  SLOVAKIA: "SK",
  SLOWAKEI: "SK",
  SLOVENIA: "SI",
  SLOWENIEN: "SI",
  CROATIA: "HR",
  KROATIEN: "HR",
  ESTONIA: "EE",
  ESTLAND: "EE",
  LATVIA: "LV",
  LETTLAND: "LV",
  LITHUANIA: "LT",
  LITAUEN: "LT",
  LUXEMBOURG: "LU",
  LUXEMBURG: "LU",
  GEORGIA: "GE"
};

// Resolve a country reading to its ISO 3166-1 alpha-2 code.
// Accepts either an already-correct code ("DE") or a country name in any
// of the spellings the geo APIs return ("Germany", "Deutschland", "Türkei").
const resolveCountryCode = (raw) => {
  const trimmed = String(raw || "").trim();
  if (!trimmed) return "";
  const upper = trimmed.toUpperCase();
  if (/^[A-Z]{2}$/.test(upper)) return upper;
  if (COUNTRY_NAME_TO_CODE[upper]) return COUNTRY_NAME_TO_CODE[upper];
  const folded = upper.normalize("NFD").replace(/[̀-ͯ]/g, "");
  if (COUNTRY_NAME_TO_CODE[folded]) return COUNTRY_NAME_TO_CODE[folded];
  return "";
};

// Convert a country reading (either an ISO 3166-1 alpha-2 code OR a full
// country name) into the corresponding regional-indicator emoji.
// Examples: "DE" → 🇩🇪, "Germany" → 🇩🇪, "Deutschland" → 🇩🇪.
// Returns a globe glyph for unknown/empty values so the hero column never
// collapses.
const countryToFlag = (raw) => {
  const code = resolveCountryCode(raw);
  if (!code) return "🌐";
  return String.fromCodePoint(
    0x1f1e6 + code.charCodeAt(0) - 65,
    0x1f1e6 + code.charCodeAt(1) - 65
  );
};

// Classify response time into a qualitative bucket for the gauge bar. Tuned
// loosely against typical SOCKS5/HTTP residential proxy latencies.
const classifyLatency = (ms) => {
  const value = Number(ms);
  if (!Number.isFinite(value) || value < 0) {
    return { label: "—", color: "#8d93a3", percent: 0, glow: "148, 163, 184" };
  }
  if (value < 600) return { label: t("Быстрый"), color: "#34d399", percent: Math.max(20, 100 - value / 8), glow: "52, 211, 153" };
  if (value < 1500) return { label: t("Средний"), color: "#fbbf24", percent: Math.max(20, 100 - value / 22), glow: "251, 191, 36" };
  if (value < 3500) return { label: t("Медленный"), color: "#fb923c", percent: Math.max(15, 100 - value / 45), glow: "251, 146, 60" };
  return { label: t("Очень медленный"), color: "#f87171", percent: 12, glow: "248, 113, 113" };
};

const ProxyChecker = ({ proxy, onCheckComplete, onDelete, isPhoneView = false }) => {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [history, setHistory] = useState([]);

  const checkProxy = async () => {
    setLoading(true);
    try {
      const data = await apiFetchJson(`/api/proxies/${proxy.id}/check`, {
        method: "POST",
        headers: { "Content-Type": "application/json" }
      });
      setResult(data);

      const newHistoryItem = {
        timestamp: new Date().toLocaleTimeString(locale(), { timeZone: "Europe/Berlin" }),
        success: data.success,
        ip: data.ip,
        location: data.location,
        responseTime: data.responseTime,
        ping: data.ping
      };

      setHistory((prev) => [newHistoryItem, ...prev.slice(0, 4)]);

      if (onCheckComplete) onCheckComplete(data);
    } catch (error) {
      const backendResult = error?.data && typeof error.data === "object" ? error.data : {};
      const backendError = backendResult.error || error?.message || t("Ошибка при проверке прокси");
      setResult({ success: false, ...backendResult, error: backendError });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (proxy.checkResult) setResult(proxy.checkResult);
  }, [proxy]);

  const formatMs = (value) => {
    const parsed = Number(value);
    if (!Number.isFinite(parsed) || parsed < 0) return "—";
    return tf`${Math.round(parsed)}мс`;
  };

  // Pick the status the card should render. Treat in-flight checks specially
  // so the visualization animates instead of going stale.
  const statusKey = loading ? "checking" : (proxy.status || "unknown");
  // Зелёный Online и красный Offline — как было: у прокси это читается
  // как «работает / не работает» с одного взгляда.
  const statusMeta = (() => {
    switch (statusKey) {
      case "active": return { label: "Online", color: "#34d399", bg: "rgba(52,211,153,0.12)", border: "rgba(52,211,153,0.28)", dot: "#34d399" };
      case "failed": return { label: "Offline", color: "#f87171", bg: "rgba(248,113,113,0.12)", border: "rgba(248,113,113,0.28)", dot: "#f87171" };
      case "checking": return { label: t("Проверка"), color: "#fbbf24", bg: "rgba(251,191,36,0.1)", border: "rgba(251,191,36,0.28)", dot: "#fbbf24" };
      default: return { label: t("Не проверен"), color: "#8d93a3", bg: "rgba(148,163,184,0.1)", border: "rgba(148,163,184,0.28)", dot: "#8d93a3" };
    }
  })();

  const country = result?.location?.country || "";
  const city = result?.location?.city || "";
  const flag = countryToFlag(country);
  // Always render the ISO code under the flag so the label stays compact
  // and consistent regardless of whether the geo API returned a name
  // ("Germany") or a code ("DE"). Falls back to the raw value if we can't
  // resolve a code (better than showing nothing for exotic countries).
  const countryCodeLabel = resolveCountryCode(country) || country;
  const latency = classifyLatency(result?.responseTime);
  const success = Boolean(result?.success);

  // Reusable styles ------------------------------------------------------
  const iconBtn = (variant) => {
    const themes = {
      primary: {
        color: A[200],
        border: aa(400, 0.36),
        bg: `linear-gradient(135deg, ${aa(500, 0.18)}, rgba(8,47,73,0.32))`,
        hover: `linear-gradient(135deg, ${aa(500, 0.32)}, ${aa(600, 0.24)})`,
        glow: `0 8px 22px -6px ${aa(500, 0.55)}`
      },
      danger: {
        color: "#fecaca",
        border: "rgba(248,113,113,0.36)",
        bg: "linear-gradient(135deg, rgba(239,68,68,0.16), rgba(127,29,29,0.24))",
        hover: "linear-gradient(135deg, rgba(239,68,68,0.28), rgba(190,18,60,0.24))",
        glow: "0 8px 22px -6px rgba(239,68,68,0.5)"
      }
    };
    return themes[variant] || themes.primary;
  };

  const dataRowChip = {
    ...PANEL_TABULAR_TEXT,
    fontSize: "12px",
    color: "#c6c9d1",
    padding: "3px 9px",
    background: `${sa(0.55)}`,
    borderRadius: "6px",
    border: "1px solid rgba(63,68,80,0.32)"
  };

  // ---- Flat 2b table-row render (variant 5a) ----
  // Текст приходит с сервера по-русски; t возвращает незнакомую строку нетронутой,
  // поэтому английские и технические сообщения не портятся.
  const rowError = result && !success ? (t(result.error) || "Connection refused") : "";
  const locText = success && (country || city) ? [countryCodeLabel || country, city].filter(Boolean).join(" · ") : "";
  const ispText = success && result?.isp ? result.isp : "";
  const exitText = success && result?.ip && result.ip !== proxy.host ? result.ip : "";
  const isBuiltin = Boolean(proxy?.isBuiltin);
  // Built-in "Umbrella" proxy: show the panel logo + friendly name instead of
  // the masked •••:••• address, so it reads as the panel's own proxy.
  const titleContent = isBuiltin
    ? (proxy.name || "Umbrella")
    : (<>{proxy.host}<span style={{ color: "#565c69" }}>:</span>{proxy.port}</>);
  const subtitleContent = isBuiltin
    ? t("Встроенный прокси")
    : String(proxy.type || "").toUpperCase();
  const ProxyGlyph = isBuiltin ? (
    <span style={{
      width: "38px", height: "38px", flexShrink: 0, borderRadius: "11px",
      overflow: "hidden",
      background: aa(400, 0.12),
      border: `1px solid ${aa(400, 0.45)}`,
      display: "inline-flex", alignItems: "center", justifyContent: "center"
    }}>
      <ArmorLogoIcon size={26} />
    </span>
  ) : (
    <span style={{
      width: "38px", height: "38px", flexShrink: 0, borderRadius: "11px",
      background: statusMeta.bg,
      border: `1px solid ${statusMeta.border}`,
      display: "inline-flex", alignItems: "center", justifyContent: "center", color: statusMeta.color
    }}>
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
        <rect x="2" y="3" width="20" height="7" rx="2" />
        <rect x="2" y="14" width="20" height="7" rx="2" />
        <line x1="6" y1="6.5" x2="6.01" y2="6.5" />
        <line x1="6" y1="17.5" x2="6.01" y2="17.5" />
      </svg>
    </span>
  );

  const statusPill = (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: "6px",
      fontSize: "12px", fontWeight: 600, color: statusMeta.color,
      background: statusMeta.bg,
      border: `1px solid ${statusMeta.border}`,
      borderRadius: "8px", padding: "4px 9px", whiteSpace: "nowrap"
    }}>
      {statusKey === "checking" ? (
        <span className="proxy-waveform" style={{ display: "inline-flex", gap: "2px", alignItems: "flex-end", height: "9px" }}>
          <i style={{ width: "2px", background: statusMeta.dot, animation: "proxy-bar 0.9s ease-in-out infinite" }} />
          <i style={{ width: "2px", background: statusMeta.dot, animation: "proxy-bar 0.9s ease-in-out infinite 0.15s" }} />
          <i style={{ width: "2px", background: statusMeta.dot, animation: "proxy-bar 0.9s ease-in-out infinite 0.3s" }} />
        </span>
      ) : (
        <span style={{ width: "6px", height: "6px", borderRadius: "9999px", background: statusMeta.dot, animation: statusKey === "active" ? "proxy-pulse 2.4s ease-in-out infinite" : "none" }} />
      )}
      {statusMeta.label}
    </span>
  );

  const pingCell = (
    <div>
      {result && success ? (
        <>
          <div style={{ ...PANEL_TABULAR_TEXT, fontSize: "13px", fontWeight: 700, color: latency.color }}>{formatMs(result.responseTime)}</div>
          <div style={{ marginTop: "5px", height: "4px", borderRadius: "9999px", background: "rgba(255,255,255,0.06)", overflow: "hidden" }}>
            <div style={{ width: `${Math.max(8, Math.min(100, latency.percent))}%`, height: "100%", background: latency.color, borderRadius: "9999px", transition: "width 0.5s cubic-bezier(0.4,0,0.2,1)" }} />
          </div>
        </>
      ) : (
        <span style={{ fontSize: "13px", color: "#565c69" }}>—</span>
      )}
    </div>
  );

  const checkBtn = (
    <button
      className="kl-plain-button"
      onClick={checkProxy}
      disabled={loading}
      title={t("Проверить прокси")}
      style={{
        width: "32px", height: "32px", flexShrink: 0, borderRadius: "10px",
        border: `1px solid ${aa(500, 0.3)}`, background: aa(500, 0.1),
        color: A[300], display: "inline-flex", alignItems: "center", justifyContent: "center",
        cursor: loading ? "not-allowed" : "pointer", opacity: loading ? 0.65 : 1, padding: 0, transition: "all 0.2s ease"
      }}
      onMouseEnter={(e) => { if (!loading) { e.currentTarget.style.background = aa(500, 0.2); e.currentTarget.style.borderColor = aa(400, 0.5); } }}
      onMouseLeave={(e) => { e.currentTarget.style.background = aa(500, 0.1); e.currentTarget.style.borderColor = aa(500, 0.3); }}
    >
      {loading ? (
        <Spinner size={13} />
      ) : (
        <RefreshIcon size={14} />
      )}
    </button>
  );

  const deleteBtn = onDelete ? (
    <button
      className="kl-plain-button"
      onClick={onDelete}
      title={t("Удалить прокси")}
      style={{
        width: "32px", height: "32px", flexShrink: 0, borderRadius: "10px",
        border: "1px solid rgba(141,147,163,0.22)", background: "transparent",
        color: "#8d93a3", display: "inline-flex", alignItems: "center", justifyContent: "center",
        cursor: "pointer", padding: 0, transition: "all 0.2s ease"
      }}
      onMouseEnter={(e) => { e.currentTarget.style.color = "#fb7185"; e.currentTarget.style.borderColor = "rgba(251,113,133,0.4)"; }}
      onMouseLeave={(e) => { e.currentTarget.style.color = "#8d93a3"; e.currentTarget.style.borderColor = "rgba(141,147,163,0.22)"; }}
    >
      <TrashIcon size={14} />
    </button>
  ) : null;

  const rowStyleTag = (
    <style jsx="true">{`
      @keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }
      @keyframes proxy-pulse { 0%, 100% { transform: scale(1); opacity: 1; } 50% { transform: scale(1.18); opacity: 0.7; } }
      @keyframes proxy-bar { 0%, 100% { height: 3px; } 50% { height: 9px; } }
    `}</style>
  );

  if (isPhoneView) {
    return (
      <div className="proxy-node-row-mobile" data-status={statusKey} style={{
        borderRadius: "14px", border: "1px solid rgba(141,147,163,0.16)", background: S.surface,
        padding: "14px 16px", display: "flex", flexDirection: "column", gap: "12px"
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
          {ProxyGlyph}
          <div style={{ minWidth: 0, flex: 1 }}>
            <div style={{ ...PANEL_TABULAR_TEXT, fontSize: "15px", fontWeight: 700, color: "#f0f1f4", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
              {titleContent}
            </div>
            <div style={{ fontSize: "11px", color: isBuiltin ? A[300] : "#707684", marginTop: "2px" }}>{subtitleContent}</div>
          </div>
          {statusPill}
        </div>
        {success && (
          <div style={{ display: "flex", flexWrap: "wrap", gap: "8px 16px", fontSize: "13px", color: "#c6c9d1" }}>
            {locText && <span style={{ display: "inline-flex", alignItems: "center", gap: "6px" }}><MapPinIcon size={13} color="#fda4af" />{locText}</span>}
            {ispText && <span style={{ display: "inline-flex", alignItems: "center", gap: "6px" }}><BuildingIcon size={13} color="#86efac" />{ispText}</span>}
            {exitText && <span style={{ ...PANEL_TABULAR_TEXT, color: "#8d93a3" }}>exit {exitText}</span>}
          </div>
        )}
        {rowError && <div style={{ fontSize: "12.5px", color: "#fca5a5" }}>{rowError}</div>}
        {!result && !loading && <div style={{ fontSize: "12px", color: "#707684", fontStyle: "italic" }}>{t("Прокси ещё не проверялся")}</div>}
        <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
          <div style={{ flex: 1 }}>{pingCell}</div>
          {checkBtn}
          {deleteBtn}
        </div>
      </div>
    );
  }

  return (
    <div
      className="proxy-node-row"
      data-status={statusKey}
      style={{
        display: "grid",
        gridTemplateColumns: "minmax(0, 1.7fr) 128px minmax(0, 1fr) minmax(0, 1.35fr) 130px 84px",
        alignItems: "center",
        gap: "14px",
        padding: "14px 20px",
        borderBottom: "1px solid rgba(141,147,163,0.08)",
        transition: "background 0.18s ease"
      }}
      onMouseEnter={(e) => { e.currentTarget.style.background = "rgba(255,255,255,0.025)"; }}
      onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}
    >
      {/* Host : Port */}
      <div style={{ display: "flex", alignItems: "center", gap: "12px", minWidth: 0 }}>
        {ProxyGlyph}
        <div style={{ minWidth: 0 }}>
          <div style={{ ...PANEL_TABULAR_TEXT, fontSize: "14px", fontWeight: 600, color: "#f0f1f4", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
            {titleContent}
          </div>
          <div style={{ fontSize: "11px", color: isBuiltin ? A[300] : "#707684", marginTop: "2px" }}>{subtitleContent}</div>
        </div>
      </div>

      {/* Status */}
      <div>{statusPill}</div>

      {/* Location / state */}
      <div style={{ fontSize: "13px", color: rowError ? "#fca5a5" : "#c6c9d1", minWidth: 0, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }} title={rowError || locText}>
        {rowError ? rowError : (locText || (loading ? t("проверка…") : (result ? "—" : t("не проверялся"))))}
      </div>

      {/* ISP / exit */}
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: "13px", color: "#c6c9d1", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{ispText || "—"}</div>
        <div style={{ ...PANEL_TABULAR_TEXT, fontSize: "11px", color: "#707684", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{exitText ? `exit ${exitText}` : (proxy.lastChecked ? `checked · ${new Date(proxy.lastChecked).toLocaleTimeString(locale(), { timeZone: "Europe/Berlin" })}` : "")}</div>
      </div>

      {/* Ping */}
      {pingCell}

      {/* Actions */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: "6px" }}>
        {checkBtn}
        {deleteBtn}
      </div>
      {rowStyleTag}
    </div>
  );
};

export default ProxyChecker;
